/**
 * My Orders page — two tabs in one screen:
 *   • Orders   — every product order (history + active)
 *   • Bookings — every service booking
 *
 * The active tab is driven by the ``?tab=`` query string so we can
 * deep-link from anywhere (e.g. Profile menu "My Bookings" item linked
 * to ``/orders?tab=bookings``).
 */
import { Link } from "react-router-dom";
import { ChevronRight, Star } from "lucide-react";
import { inr2 } from "../../lib/api";
import { FullPageLoader } from "../../components/Loaders";
import { useIsBlinkit } from "../../lib/storefrontTheme";
import { useOrdersController } from "./controllers/useOrdersController";
import { BookingModals, BookingsList, EmptyState } from "./_partials/BookingsPanel";
import BkOrdersView from "./blinkit/BkOrdersView";
import { OrderGroupChip } from "../../components/MultiShop";

export default function OrderHistoryPage() {
    return useIsBlinkit() ? <BkOrdersView /> : <ClassicOrderHistoryPage />;
}

function ClassicOrderHistoryPage() {
    const {
        tab, setTab, orders, bookings, counts, cancelBooking, paying, setPaying, openPay, submitPay, reviewing,
        setReviewing, openReview, submitReview,
    } = useOrdersController();

    return (
        <div className="pb-32">
            <header className="px-4 pt-5 pb-3 bg-card border-b border-border">
                <h1 className="text-2xl font-display font-bold">My Orders</h1>
                <div className="mt-3 flex gap-1 p-0.5 bg-muted rounded-lg w-fit" data-testid="orders-tabs">
                    <TabBtn active={tab === "orders"} onClick={() => setTab("orders")} testid="orders-tab-orders">
                        Orders{counts.orders != null && counts.orders > 0 ? ` · ${counts.orders}` : ""}
                    </TabBtn>
                    <TabBtn active={tab === "bookings"} onClick={() => setTab("bookings")} testid="orders-tab-bookings">
                        Bookings{counts.bookings != null && counts.bookings > 0 ? ` · ${counts.bookings}` : ""}
                    </TabBtn>
                </div>
            </header>

            {tab === "orders" ? (
                orders === null ? (
                    <FullPageLoader />
                ) : orders.length === 0 ? (
                    <EmptyState testid="orders-empty" title="No orders yet" cta="Browse menu" ctaTo="/" />
                ) : (
                    <div className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="orders-list">
                        {orders.map((o) => (
                            <Link key={o.id} to={`/orders/${o.id}`} data-testid={`order-${o.id}`} className="block bg-card rounded-2xl border border-border p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="font-bold text-sm">{o.restaurant_name}</p>
                                        <p className="text-xs text-muted-foreground">{o.order_unique_id} · {new Date(o.created_at).toLocaleDateString()}</p>
                                    </div>
                                    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full ${o.status === "DELIVERED" ? "bg-emerald-100 text-emerald-700" : o.status === "CANCELLED" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{o.status}</span>
                                </div>
                                <div className="mt-2 text-xs text-muted-foreground line-clamp-1">
                                    {o.items.map((i) => `${i.name} x${i.quantity}`).join(", ")}
                                </div>
                                {/* Only on an order that came from a multi-shop checkout. */}
                                <OrderGroupChip order={o} className="mt-2" />
                                <div className="mt-2 flex items-center justify-between">
                                    <span className="font-bold text-sm">{inr2(o.totals?.grand_total)}</span>
                                    {o.status === "DELIVERED" && (
                                        <span data-testid={`rate-hint-${o.id}`} className="text-[11px] font-bold text-amber-700 dark:text-amber-300 inline-flex items-center gap-1">
                                            <Star size={12} fill="currentColor" /> Rate &amp; review
                                        </span>
                                    )}
                                    <ChevronRight size={16} className="text-muted-foreground" />
                                </div>
                            </Link>
                        ))}
                    </div>
                )
            ) : (
                <BookingsList bookings={bookings} cancelBooking={cancelBooking} openPay={openPay} openReview={openReview} />
            )}

            <BookingModals paying={paying} setPaying={setPaying} submitPay={submitPay} reviewing={reviewing} setReviewing={setReviewing} submitReview={submitReview} />
        </div>
    );
}

function TabBtn({ active, onClick, testid, children }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`px-3 py-1.5 text-xs font-bold rounded-md ${active ? "bg-card text-foreground shadow" : "text-muted-foreground"}`}
        >{children}</button>
    );
}
